import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "wouter";
import type { BlogPost } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Loader2, AlertTriangle, ArrowLeft, ArrowRight, Calendar, User } from "lucide-react";
import { useSEO } from "@/hooks/use-seo";
import { marketingTitle } from "@/data/marketing-seo";
import {
  blogPostAuthor,
  blogPostCorePage,
  blogPostDate,
  blogPostDescription,
  blogPostPath,
  blogPostTitle,
  blogPostingJsonLd,
  formatBlogDate,
} from "@/data/blog";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";
import DOMPurify from "dompurify";

export default function BlogPostPage() {
  const { id } = useParams<{ id: string }>();

  const { data: post, isLoading, error } = useQuery<BlogPost>({
    queryKey: ["/api/blog-posts", id],
    queryFn: async () => {
      const res = await fetch(`/api/blog-posts/${id}`);
      if (!res.ok) throw new Error("Failed to load post");
      return res.json();
    },
    enabled: !!id,
  });

  useSEO({
    title: post ? blogPostTitle(post) : marketingTitle("Blog"),
    description: post ? blogPostDescription(post) : "Read this article on the PadPulse blog.",
    ogType: "article",
    path: post ? blogPostPath(post) : id ? `/blog/${id}` : "/blog",
    jsonLd: post ? blogPostingJsonLd(post) : undefined,
  });

  const author = post ? blogPostAuthor(post) : null;
  const corePage = post ? blogPostCorePage(post) : null;

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <PublicHeader />
      <div className="bg-gradient-to-br from-[#0a1628] via-[#0d2447] to-brand pt-20 lg:pt-20">
      </div>

      <main className="flex-grow">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="post-loading">
            <Loader2 className="h-10 w-10 animate-spin text-brand mb-4" />
            <p className="text-gray-500 font-medium">Loading article...</p>
          </div>
        ) : error || !post ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center" data-testid="post-error">
            <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mb-6">
              <AlertTriangle className="h-8 w-8 text-amber-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Article Not Found</h1>
            <p className="text-gray-500 mb-6 max-w-md text-sm">
              The article you are looking for might have been removed or is temporarily unavailable.
            </p>
            <Link href="/blog">
              <Button variant="outline" className="border-gray-200 text-gray-700" data-testid="button-back-blog">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
              </Button>
            </Link>
          </div>
        ) : (
          <>
            {post.imageUrl && (
              <div className="w-full h-64 md:h-80 overflow-hidden relative">
                <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628]/40 to-[#0a1628]/70 z-10" />
                <img
                  src={post.imageUrl}
                  alt={post.title}
                  className="w-full h-full object-cover"
                  data-testid="img-post-hero"
                />
              </div>
            )}

            <section className="bg-gray-50 pt-10 pb-12">
              <div className="container mx-auto px-6 max-w-4xl">
                <Link href="/blog" className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-brand mb-6 transition-colors" data-testid="link-back">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to all articles
                </Link>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight tracking-tight" data-testid="text-post-title">
                  {post.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4">
                  {author && (
                    <div className="flex items-center text-gray-500 text-sm" data-testid="text-post-author">
                      <User className="w-4 h-4 mr-2" />
                      {author}
                    </div>
                  )}
                  <div className="flex items-center text-gray-500 text-sm" data-testid="text-post-date">
                    <Calendar className="w-4 h-4 mr-2" />
                    {formatBlogDate(blogPostDate(post))}
                  </div>
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {post.tags.map((tag) => (
                        <span key={tag} className="text-xs bg-blue-50 text-brand px-2.5 py-0.5 rounded-md font-medium" data-testid={`tag-${tag}`}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section className="py-12 md:py-16">
              <div className="container mx-auto px-6 max-w-3xl">
                <article className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-600 prose-p:leading-relaxed prose-a:text-brand" data-testid="post-content">
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }} />
                </article>
                {corePage && (
                  <Link
                    href={corePage.path}
                    className="mt-12 flex items-center justify-between gap-4 rounded-2xl border border-brand/20 bg-brand/5 p-5 text-gray-900 hover:bg-brand/10"
                    data-testid="link-post-core-page"
                  >
                    <span>
                      <span className="block text-xs font-semibold uppercase tracking-wider text-brand">{corePage.name}</span>
                      <span className="mt-1 block text-lg font-semibold">{corePage.h1}</span>
                    </span>
                    <ArrowRight className="h-5 w-5 shrink-0 text-brand" />
                  </Link>
                )}
                <div className="mt-16 pt-8 border-t border-gray-100">
                  <Link href="/blog">
                    <Button variant="outline" className="border-gray-200 text-gray-700 hover:bg-gray-50" data-testid="button-back-bottom">
                      <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
                    </Button>
                  </Link>
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      <PublicFooter />
    </div>
  );
}
